import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/features/auth/AuthContext'
import { CompanyAutocomplete } from '@/features/home/components/CompanyAutocomplete'
import type { CompanyValues } from '@/features/home/types'

// Mirror of backend ALLOWED_EMAIL_DOMAINS (backend is authoritative; this is UX).
const ALLOWED_EMAIL_DOMAINS = [
  'activum.cz',
  'brokerteam.cz',
  'crcredit.cz',
  'cicplus.cz',
  'petrisk.cz',
  'isgroup.cz',
  'npsg.cz',
  'kalkulator.cz',
  'czechinsurance.cz',
  'vib.cz',
  'greco.services',
  'jcias.cz',
  'limmit.cz',
  'remespartners.cz',
  'tichyconsulting.com',
  'frenkee.cz',
] as const

function isAllowedEmail(email: string): boolean {
  const at = email.lastIndexOf('@')
  if (at < 0) return false
  const domain = email.slice(at + 1).trim().toLowerCase()
  return (ALLOWED_EMAIL_DOMAINS as readonly string[]).includes(domain)
}

const schema = z
  .object({
    email: z
      .string()
      .email('Neplatný e-mail')
      .refine(isAllowedEmail, {
        message: 'Registrace je omezena na schválené firemní e-mailové domény.',
      }),
    name: z.string().min(1, 'Vyplňte jméno'),
    company: z.string().optional(),
    ico: z.string().optional(),
    phone: z.string().optional(),
    password: z.string().min(6, 'Minimálně 6 znaků'),
    password_confirm: z.string(),
    gdpr: z.boolean().refine((v) => v, { message: 'Musíte souhlasit s GDPR' }),
    newsletter: z.boolean().optional(),
  })
  .refine((d) => d.password === d.password_confirm, {
    path: ['password_confirm'],
    message: 'Hesla se neshodují',
  })

type FormValues = z.infer<typeof schema>

export function RegisterPage() {
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { gdpr: false, newsletter: false },
  })

  async function onSubmit(values: FormValues) {
    setSubmitError(null)
    try {
      const fallbackName = values.name || values.company || values.email.split('@')[0]
      await registerUser(values.email, fallbackName, values.password)
      navigate('/', { replace: true })
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
      let m = 'Registrace selhala.'
      if (typeof msg === 'string') m = msg
      else if (Array.isArray(msg) && msg[0] && typeof msg[0] === 'object' && 'msg' in msg[0]) {
        m = String((msg[0] as { msg: string }).msg)
      }
      setSubmitError(m)
    }
  }

  const gdpr = watch('gdpr')
  const newsletter = watch('newsletter')

  return (
    <div className="min-h-screen bg-zinc-50 px-4 py-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="rounded-lg bg-gradient-to-r from-purple-500 to-orange-400 px-6 py-8 text-white">
          <h2 className="text-2xl font-semibold">Registrace účtu</h2>
          <p className="text-sm opacity-90 mt-1">Vytvořte si účet a začněte pracovat s vozidly.</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Údaje o účtu</CardTitle>
            <CardDescription>Vyplňte základní informace.</CardDescription>
          </CardHeader>
          <form onSubmit={handleSubmit(onSubmit)}>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="E-mail" error={errors.email?.message} htmlFor="email">
                  <Input id="email" type="email" autoComplete="email" {...register('email')} />
                </Field>
                <Field label="Jméno" error={errors.name?.message} htmlFor="name">
                  <Input id="name" {...register('name')} />
                </Field>
                <Field label="Název firmy" htmlFor="company">
                  <CompanyAutocomplete
                    value={{ COMPANY_NAME: watch('company') || '', COMPANY_REGISTRATION_NUMBER: watch('ico') || '' } as CompanyValues}
                    onChange={(c) => {
                      setValue('company', c?.COMPANY_NAME ?? '')
                      setValue('ico', c?.COMPANY_REGISTRATION_NUMBER ?? '')
                    }}
                    placeholder="Začněte psát název firmy nebo IČO"
                  />
                </Field>
                <Field label="IČO" htmlFor="ico">
                  <Input id="ico" inputMode="numeric" {...register('ico')} />
                </Field>
                <Field label="Telefon" htmlFor="phone">
                  <Input id="phone" type="tel" {...register('phone')} />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Heslo" error={errors.password?.message} htmlFor="password">
                  <Input id="password" type="password" autoComplete="new-password" {...register('password')} />
                </Field>
                <Field label="Potvrzení hesla" error={errors.password_confirm?.message} htmlFor="password_confirm">
                  <Input id="password_confirm" type="password" autoComplete="new-password" {...register('password_confirm')} />
                </Field>
              </div>

              <div className="space-y-3">
                <label className="flex items-start gap-2 text-sm text-zinc-700">
                  <Checkbox checked={gdpr} onCheckedChange={(v) => setValue('gdpr', !!v, { shouldValidate: true })} />
                  <span>
                    Souhlasím se zpracováním osobních údajů (GDPR) a se{' '}
                    <a href="#" className="text-brand hover:underline">smluvními podmínkami</a>.
                  </span>
                </label>
                {errors.gdpr && <p className="text-sm text-red-600">{errors.gdpr.message}</p>}
                <label className="flex items-start gap-2 text-sm text-zinc-700">
                  <Checkbox checked={newsletter} onCheckedChange={(v) => setValue('newsletter', !!v)} />
                  <span>Chci dostávat novinky o produktu.</span>
                </label>
              </div>

              {submitError && (
                <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{submitError}</p>
              )}
            </CardContent>
            <CardFooter className="flex items-center justify-between">
              <Button type="submit" disabled={isSubmitting} variant="default">
                {isSubmitting ? 'Vytvářím účet…' : 'Vytvořit účet'}
              </Button>
              <Link to="/login" className="text-sm text-zinc-600 hover:text-zinc-800">
                Máte účet? Přihlásit se
              </Link>
            </CardFooter>
          </form>
        </Card>
      </div>
    </div>
  )
}

function Field({
  label,
  error,
  htmlFor,
  children,
}: {
  label: string
  error?: string
  htmlFor?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}
